import { parseToDate } from './dateUtils';

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Normaliza sguTrial para o fim do dia local (último instante com acesso).
 * Datas só com dia (YYYY-MM-DD) usam fuso local para evitar deslocamento UTC.
 */
export const parseTrialEndDate = (sguTrial) => {
  if (sguTrial == null || sguTrial === '') return null;

  const raw = String(sguTrial).trim();
  const dateOnly = raw.match(DATE_ONLY_PATTERN);

  if (dateOnly) {
    const year = parseInt(dateOnly[1], 10);
    const month = parseInt(dateOnly[2], 10);
    const day = parseInt(dateOnly[3], 10);
    const end = new Date(year, month - 1, day, 23, 59, 59, 999);
    return Number.isNaN(end.getTime()) ? null : end;
  }

  const date = parseToDate(sguTrial);
  if (!date) return null;
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
};

/**
 * Usuário com trial configurado e data já vencida.
 * Sem sguTrial (null/undefined) não há restrição de trial.
 */
export const isTrialExpired = (userOrTrialDate) => {
  const sguTrial =
    userOrTrialDate && typeof userOrTrialDate === 'object'
      ? userOrTrialDate.sguTrial
      : userOrTrialDate;

  if (sguTrial == null || sguTrial === '') return false;

  const trialEnd = parseTrialEndDate(sguTrial);
  if (!trialEnd) return false;

  return Date.now() > trialEnd.getTime();
};

export const getTrialExpiredLoginMessage = (sguTrial) => {
  const trialEnd = parseTrialEndDate(sguTrial);
  const formattedDate = trialEnd
    ? trialEnd.toLocaleDateString('pt-BR')
    : '';

  if (formattedDate) {
    return `Seu período de avaliação do SGU encerrou em ${formattedDate}. Entre em contato com o suporte para renovar o acesso.`;
  }

  return 'Seu período de avaliação do SGU encerrou. Entre em contato com o suporte para renovar o acesso.';
};

/**
 * Retorna mensagem de erro se o trial estiver expirado; caso contrário null.
 */
export const getTrialLoginBlockMessage = (userData) => {
  if (!isTrialExpired(userData)) return null;
  const sguTrial =
    userData && typeof userData === 'object' ? userData.sguTrial : userData;
  return getTrialExpiredLoginMessage(sguTrial);
};

export const getApiErrorMessage = (data, fallback = 'Ocorreu um erro. Tente novamente.') => {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data !== 'object') return String(data);

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (data.trialEndDatePtBr) {
    return `Seu período de avaliação do SGU encerrou em ${data.trialEndDatePtBr}. Entre em contato com o suporte para renovar o acesso.`;
  }

  if (data.sguTrial) {
    return getTrialExpiredLoginMessage(data.sguTrial);
  }

  return fallback;
};

/**
 * Retorna dados do trial ativo para exibição na interface; null se não houver trial ou já expirou.
 */
export const getActiveTrialInfo = (userData) => {
  const sguTrial = userData?.sguTrial;
  if (sguTrial == null || sguTrial === '') return null;
  if (isTrialExpired(userData)) return null;

  const trialEnd = parseTrialEndDate(sguTrial);
  if (!trialEnd) return null;

  const formattedDate = trialEnd.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfEndDay = new Date(trialEnd);
  startOfEndDay.setHours(0, 0, 0, 0);
  const daysRemaining = Math.round(
    (startOfEndDay - startOfToday) / (1000 * 60 * 60 * 24)
  );

  let message;
  if (daysRemaining <= 0) {
    message = `Você está em período de avaliação. Seu acesso ao SGU termina hoje (${formattedDate}).`;
  } else if (daysRemaining === 1) {
    message = `Você está em período de avaliação. Seu acesso ao SGU está disponível até amanhã (${formattedDate}).`;
  } else {
    message = `Você está em período de avaliação. Seu acesso ao SGU está disponível até ${formattedDate} (restam ${daysRemaining} dias).`;
  }

  return {
    formattedDate,
    daysRemaining,
    message,
  };
};
